import type { CountryCode, Movie } from '@filmsloth/shared';
import { PROVIDER_INFO, providerLinks } from '@filmsloth/shared';
import { useMovieTitle, useT } from '../i18n/index.js';

/**
 * "Where can we actually watch this."
 *
 * Every link is a search on the provider, never a constructed title URL — the
 * catalog does not know provider-side ids, and a guessed one 404s. Where even
 * the search pattern is unknown the link reaches the service's front page and
 * says so, rather than pretending to find the film.
 */
export function WatchOnLinks({ movie, country }: { movie: Movie; country: CountryCode }) {
  const t = useT();
  const movieTitle = useMovieTitle();
  const providers = movie.availability[country] ?? [];
  if (providers.length === 0) return null;

  const title = movieTitle(movie);
  const links = providerLinks(providers, title);

  return (
    <div className="watch-on">
      <div className="small muted">{t('watchOn.title')}</div>
      <div className="chips">
        {links.map((link) => (
          <a
            key={link.provider}
            className="chip chip--link"
            href={link.url}
            target="_blank"
            // `noopener` matters here: without it the opened tab gets a handle
            // on this one and can navigate it away.
            rel="noopener noreferrer"
            title={
              link.kind === 'search'
                ? t('watchOn.searchHint', { provider: link.name, title })
                : t('watchOn.homeHint', { provider: link.name })
            }
          >
            <span
              className="chip__dot"
              style={{ background: PROVIDER_INFO[link.provider].color }}
            />
            {link.name}
            {link.kind === 'home' ? <span className="chip__note">↗</span> : null}
          </a>
        ))}
      </div>
    </div>
  );
}
